<template>
  <div class="w-full px-6 py-4">
    <div v-if="customer.isNewRecord">
      <customer-duplicate
        :firstName="_customer.firstName"
        :lastName="_customer.lastName"
        :email="_customer.email"
        class="mb-5"
      />
    </div>

    <div class="mb-5 flex items-center">
      <o-icon icon="customerEdit" :size="20" class="mr-3 opacity-75" />
      <div>
        <p class="leading-none mb-1">
          {{ $t('general.personalInfo') }}
        </p>
        <p class="leading-none text-sm opacity-60">
          {{ $t('general.nameAndContact') }}
        </p>
      </div>
    </div>

    <div>
      <div class="flex space-x-4 mb-4">
        <o-field-group style="width: 130px;" :label="$t('general.title')">
          <title-field v-model="_customer.title" :label="$t('general.title')" />
        </o-field-group>
        <o-field-group
          v-slot="{ status }"
          class="w-full md:w-1/2"
          :label="$t('general.firstName')"
          :name="fieldNames.firstName"
          rules="required"
          :validation-value="_customer.firstName"
        >
          <text-field v-model="_customer.firstName" :label="$t('general.firstName')" :status="status" />
        </o-field-group>
        <o-field-group
          v-slot="{ status }"
          class="w-full md:w-1/2"
          :label="$t('general.lastName')"
          :name="fieldNames.lastName"
          rules="required"
          :validation-value="_customer.lastName"
        >
          <text-field v-model="_customer.lastName" :label="$t('general.lastName')" :status="status" />
        </o-field-group>
      </div>
      <div class="flex space-x-4 mb-4">
        <avatar-field v-if="!_customer.isNewRecord" :model="_customer" @click="handleUpdateImage" />
      </div>
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.company')">
          <text-field v-model="_customer.company" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.occupation')">
          <text-field v-model="_customer.occupation" />
        </o-field-group>
      </div>
      <div class="flex space-x-4 mb-4">
        <o-field-group
          v-slot="{ status }"
          class="w-full md:w-1/2"
          :label="$t('general.email')"
          :name="fieldNames.email"
          rules="email"
          :validation-value="_customer.email"
        >
          <text-field v-model="_customer.email" type="email" icon="MailIcon" :status="status" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.mobileCell')">
          <text-field v-model="_customer.phone" icon="SmartphoneIcon" />
        </o-field-group>
      </div>
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full md:w-1/2" :label="$t('general.homePhone')">
          <text-field v-model="_customer.homePhone" icon="PhoneIcon" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.workPhone')">
          <text-field v-model="_customer.workPhone" icon="PhoneIcon" />
        </o-field-group>
      </div>
      <div class="flex space-x-4 mb-4">
        <o-field-group
          v-if="!_customer.isNewRecord && _customer.ageVerified"
          class="w-full md:w-1/2"
          :label="$t('general.dateOfBirth')"
          :name="$t('general.dateOfBirth')"
        >
          <div
            class="bg-gray-50 dark:bg-gray-800 rounded-md flex items-center justify-between px-3"
          >
            <span class="flex items-center">
              <o-chip
                v-if="customer.ageVerified"
                class="mr-2"
                size="sm"
                variant="success"
                icon="success"
              >
                {{ $t('customer.verification.verified') }} - {{ _customer.ageVerifiedDetail.verifiedViaLabel }}
              </o-chip>

              <p class="text-sm py-[10px]">
                {{ _customer.dateOfBirthLabel || '-' }} <span v-if="ageLabel" class="pl-2 opacity-70">({{ ageLabel }})</span>
              </p>
            </span>

            <o-button
              size="sm"
              icon="edit"
              transparent
              @click="handleChangeDateOfBirth"
            />
          </div>
        </o-field-group>
        <o-field-group v-else class="w-full md:w-1/2" :label="$t('general.dateOfBirth')" date :message="ageLabel">
          <o-tooltip :text="_customer.isNewRecord ? $t('components.customer.edit.information.dateOfBirth') : $t('components.customer.edit.information.verifyDateOfBirth')">
            <date-field
              v-model="_dateOfBirth"
              :augmenter="Augmenters.YMD"
              visibility="focus"
            >
              <template #trailing>
                <o-button
                  v-if="!_customer.isNewRecord"
                  size="sm"
                  class="-mr-1"
                  transparent
                  @click="handleChangeDateOfBirth"
                >
                  {{ $t('general.verify') }}
                </o-button>
              </template>
            </date-field>
          </o-tooltip>
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('general.gender')">
          <!--<select-field
            :value="customer.gender.value"
            :options="customerGenderOptions"
            :label="$t('general.gender')"
            @input="_customer.gender = CustomerGender.symbolForValue($event)"
          />-->
          <select-field
            v-model="_customer.gender"
            :options="customerGenderOptions"
            :label="$t('general.gender')"
          />
        </o-field-group>
      </div>
      <div v-if="!customer.isNewRecord" class="flex space-x-4 mb-4">
        <o-field-group v-if="$features.get('loyalty')" class="w-full md:w-1/2" :label="$t('components.customer.edit.information.loyaltyPointBalance')">
          <number-field v-model="_loyaltyPoints" :label="$t('components.customer.edit.information.loyaltyPointBalance')" />
        </o-field-group>
        <o-field-group class="w-full md:w-1/2" :label="$t('components.customer.edit.information.creditBalance')">
          <number-field v-model="_credit" :label="$t('components.customer.edit.information.creditBalance')" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full" :label="$t('general.notes')">
          <text-area-field v-model="_customer.notes" :label="$t('general.notes')" rows="5" />
        </o-field-group>
      </div>

      <div v-if="customer.isNewRecord" class="mb-4 p-4 rounded border bg-gray-50 dark:bg-gray-800/20  dark:border-gray-800">
        <o-field-group v-if="customerPrivacyConsentInfo.enabled" class="w-full mb-4 block" :label="customerPrivacyConsentInfo.title" :rules="{ required: { allowFalse: false } }">
          <checkbox-field v-model="_privacyConsentAccepted" :label="customerPrivacyConsentInfo.label" :info="customerPrivacyConsentInfo.content" />
        </o-field-group>

        <o-field-group v-if="customerMarketingConsentInfo.enabled" class="w-full" :label="customerMarketingConsentInfo.title">
          <checkbox-field v-model="_marketingConsentAccepted" :label="customerMarketingConsentInfo.label" :info="customerMarketingConsentInfo.content" />
        </o-field-group>
      </div>
    </div>

    <update-image-dialog
      ref="updateImageDialog"
      @update="handleUpdateImageSuccess"
    />

    <change-date-of-birth-modal
      ref="changeDateOfBirthModal"
      @update="handleChangeDateOfBirthSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import {
  CustomerGender,
  CustomerGenderLabels,
  CustomerMarketingConsent,
  CustomerPrivacyConsent,
} from '~/_api/models/customer'
import { DateYMD } from '~/_api/lib/dates'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import CustomerDuplicate from '@/components/customer/elements/Duplicate.vue'
import TitleField from '@/components/customer/elements/Title.vue'
import UpdateImageDialog from '@/components/customer/actions/Image.vue'
import ChangeDateOfBirthModal from '@/components/customer/actions/ChangeDateOfBirth.vue'

import {
  DateField,
  SelectField,
  CheckboxField,
  CheckboxGroupField,
  RadioGroupField,
  TextField,
  TextAreaField,
  NumberField,
  AvatarField,
} from '@/components/fields'

interface Props {
  customer: Record<string, any>
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:customer', value: any): void
}>()

const { $bus, $i18n, $auth, $date, $dateFns } = useNuxtApp()

const fieldNames = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
} as const

const updateImageDialog = ref<any>()
const changeDateOfBirthModal = ref<any>()

const _customer = computed({
  get () {
    return props.customer
  },
  set (value) {
    emit('update:customer', value)
  },
})

const _loyaltyPoints = computed({
  get () {
    return _customer.value.balances?.loyaltyPoints
  },
  set (value) {
    _customer.value.balances.loyaltyPoints = value
    _customer.value.loyaltyPointBalance = value
  },
})

const _credit = computed({
  get () {
    return _customer.value.balances?.credit
  },
  set (value) {
    _customer.value.balances.credit = value
    _customer.value.creditBalance = value
  },
})

const _dateOfBirth = computed({
  get () {
    return _customer.value.dateOfBirth?.value ?? null
  },
  set (value) {
    _customer.value.dateOfBirth = value != null ? DateYMD.fromInteger(value) : null
  },
})

const _marketingConsentAccepted = computed({
  get () {
    return props.customer.marketingConsentAccepted === CustomerMarketingConsent.accepted
  },
  set (value) {
    _customer.value.marketingConsentAccepted = value
      ? CustomerMarketingConsent.accepted
      : CustomerMarketingConsent.declined
  },
})

const _privacyConsentAccepted = computed({
  get () {
    return props.customer.consentAccepted === CustomerPrivacyConsent.accepted
  },
  set (value) {
    _customer.value.consentAccepted = value
      ? CustomerPrivacyConsent.accepted
      : CustomerPrivacyConsent.declined
  },
})

const customerGenderOptions = computed(() => {
  return Object.values(CustomerGender)
    .map((value) => {
      return {
        label: $i18n.t(CustomerGenderLabels[value as CustomerGender]),
        value,
      }
    })
})

const customerPrivacyConsentInfo = computed(() => {
  const location = ($auth as any).info.location

  return {
    enabled: location.customerConsentEnabled,
    title: location.customerConsentTitle,
    label: location.customerConsentAcceptLabel,
    content: location.customerConsentContent,
  }
})

const customerMarketingConsentInfo = computed(() => {
  const location = ($auth as any).info.location

  return {
    enabled: location.customerMarketingConsentEnabled,
    title: location.customerMarketingConsentTitle,
    label: location.customerMarketingConsentAcceptLabel,
    content: location.customerMarketingConsentContent,
  }
})

const ageLabel = computed(() => {
  if (!_customer.value.dateOfBirth?.value) {
    return ''
  }

  const date = ($date as any).parse(_customer.value.dateOfBirth.value.toString(), 'yyyyMMdd')
  const age = ($dateFns as any).differenceInYears(new Date(), date)

  return $i18n.t('components.customer.actions.changeDateOfBirth.ageLabel', { age })
})

const handleUpdateImage = () => {
  updateImageDialog.value.open(props.customer)
}

const handleUpdateImageSuccess = (customer: any) => {
  _customer.value.photos = customer.photos
  _customer.value.profilePhoto = customer.profilePhoto
  $bus.emit.updated('Customer', customer)
}

const handleChangeDateOfBirth = () => {
  changeDateOfBirthModal.value.open(props.customer)
}

const handleChangeDateOfBirthSuccess = (customer: any) => {
  _customer.value.dateOfBirth = customer.dateOfBirth
  _customer.value.ageVerified = customer.ageVerified
  _customer.value.ageVerifiedDetail = customer.ageVerifiedDetail
  $bus.emit.updated('Customer', customer)
}
</script>
